// CSV exports and the invoice summary. The same files as the Mac app (apple/Tally/Services/CSVExporter.swift):
// one row per merged billable interval, so a file matches the timeline to the second; rounding happens only here.
import {
  billProjects, clipIntervals, formatClock, invoiceSummaries, roundForExport, amountCents, tokenCosts, type Interval, type InvoiceSummary,
  type ProjectTotals, type RoundedExport, type TokenCostLine,
} from '../../../packages/core/src/index.ts';
import type { Client, InvoiceLineInput, Project, TimeEntry } from '../api/types.ts';
import type { Formats } from '../lib/format.ts';
import { isoDate, localParts } from '../lib/calendar.ts';
import type { Lang, Translate } from '../lib/i18n.ts';
import { agentPieces, billingMember, billingProjects, client, displayTitle, providerInfo, tokenSettings, type RangeData, type Workspace } from './data.ts';
import { groupingLabel, groupingOverlaps, type ReportModel } from './report.ts';
import { totalTokens, usageSummary } from './session.ts';

export interface CsvRow {
  interval: Interval;
  /** Whose time (teams: each member's intervals merge on their own, spec §9b). */
  member: string | null;
  humanMs: number;
  agentMs: number;
  sources: string[];
  descriptions: string[];
  roundedMs: number;
}

export interface ProjectExport {
  project: Project;
  client: Client | undefined;
  range: Interval;
  rows: CsvRow[];
  totals: ProjectTotals | undefined;
  rounded: RoundedExport | undefined;
  billedMs: number;
  rawMs: number;
  /** Member names for the member column (only when more than one member worked on the project). */
  memberNames: Map<string, string>;
}

/** More than one member in the rows: the file gets a member column. */
export const isTeamExport = (x: ProjectExport): boolean => x.rows.some((r) => (r.member ?? '') !== (x.rows[0]?.member ?? ''));

/** Agent time is the same as on screen and in reports: working spans with corrections applied (spec §9a). */
export function buildProjectExport(t: Translate, ws: Workspace, data: RangeData, projectId: string, range: Interval, now: number): ProjectExport | null {
  const p = ws.projects.find((x) => x.id === projectId);
  if (!p) return null;
  const entries = data.entries.filter((e) => e.projectId === p.id && e.deletedAt === null);
  const sessions = new Map(data.sessions.map((s) => [s.id, s]));
  const pieces = agentPieces(data, now).filter((x) => x.project === p.id);
  const project = billingProjects(ws).filter((b) => b.id === p.id);
  const billingEntries = entries.map((e) => ({ project: p.id, start: e.startAt, end: e.endAt, billable: e.billable, member: billingMember(data, e.memberId) }));
  const billingSpans = pieces.map((x) => ({ project: p.id, start: x.start, end: x.end, member: x.member ?? null }));
  const totals = billProjects({ projects: project, entries: billingEntries, agentSpans: billingSpans, now, range }).get(p.id);
  const rounded = totals ? roundForExport(totals.billableIntervals, p.roundingMinutes, p.rateCents) : undefined;
  const step = p.roundingMinutes * 60_000;
  // One member at a time, so each row's own and agent time are that member's (spec §9b).
  const members = [...new Set([...billingEntries, ...billingSpans].map((x) => x.member ?? ''))].sort();
  const intervals = members.flatMap((member) => (billProjects({
    projects: project, now, range,
    entries: billingEntries.filter((e) => (e.member ?? '') === member),
    agentSpans: billingSpans.filter((s) => (s.member ?? '') === member),
  }).get(p.id)?.billableIntervals ?? []).map((interval) => ({ member, interval })))
    .sort((a, b) => a.interval.start - b.interval.start || a.interval.end - b.interval.end);
  const rows = intervals.map(({ member, interval }): CsvRow => {
    const human: { entry: TimeEntry; part: Interval }[] = [];
    for (const e of entries) {
      if (!e.billable || (billingMember(data, e.memberId) ?? '') !== member) continue;
      const [part] = clipIntervals([{ start: e.startAt, end: e.endAt ?? now }], interval.start, interval.end);
      if (part) human.push({ entry: e, part });
    }
    const agent: { session: string; part: Interval }[] = [];
    for (const x of pieces) {
      if ((x.member ?? '') !== member) continue;
      const [part] = clipIntervals([{ start: x.start, end: x.end }], interval.start, interval.end);
      if (part) agent.push({ session: x.session, part });
    }
    const sources: string[] = [];
    if (human.length > 0) sources.push(t('Eigene Zeit'));
    const names = new Set(agent.map((a) => sessions.get(a.session)).filter((s) => s !== undefined).map((s) => providerInfo(s.provider).name));
    sources.push(...[...names].sort());
    const descriptions = [...new Set(human.map((h) => h.entry.description).filter((d) => d.length > 0))].sort();
    const titles = new Set(agent.map((a) => sessions.get(a.session)).filter((s) => s !== undefined).map((s) => displayTitle(s)));
    descriptions.push(...[...titles].sort());
    const d = interval.end - interval.start;
    return {
      interval, sources, descriptions, member: member || null,
      humanMs: human.reduce((n, h) => n + h.part.end - h.part.start, 0),
      agentMs: agent.reduce((n, a) => n + a.part.end - a.part.start, 0),
      roundedMs: step > 0 ? Math.ceil(d / step) * step : d,
    };
  });
  return {
    project: p, client: client(ws, p.clientId), range, rows, totals, rounded,
    billedMs: totals?.billableMs ?? 0,
    rawMs: rows.reduce((n, r) => n + r.humanMs + r.agentMs, 0),
    memberNames: new Map(ws.members.map((m) => [m.id, m.name])),
  };
}

function csvTools(lang: Lang) {
  const german = lang === 'de';
  const separator = german ? ';' : ',';
  const decimal = (value: number, digits: number) => {
    const s = value.toFixed(digits);
    return german ? s.replace('.', ',') : s;
  };
  const field = (s: string) => (s.includes(separator) || s.includes('"') || s.includes('\n') ? `"${s.replaceAll('"', '""')}"` : s);
  const line = (cells: string[]) => cells.map(field).join(separator);
  return { separator, decimal, line };
}

/** `H:MM:SS` plus milliseconds when there are any (to the millisecond, like the timeline). */
export function exactDuration(ms: number): string {
  const rest = Math.max(0, ms) % 1000;
  return rest === 0 ? formatClock(ms) : `${formatClock(ms)}.${String(rest).padStart(3, '0')}`;
}

/** German exports use `;` and decimal commas (Excel DE); English uses `,` and points. UTF-8 with BOM. */
export function projectCsv(t: Translate, x: ProjectExport, lang: Lang, timeZone: string): string {
  const { decimal, line } = csvTools(lang);
  const hours = (ms: number) => decimal(ms / 3_600_000, 4);
  const money = (cents: number) => decimal(cents / 100, 2);
  const clock = (v: number) => {
    const p = localParts(v, timeZone);
    return [p.hour, p.minute, p.second].map((n) => String(n).padStart(2, '0')).join(':');
  };
  const rate = x.project.rateCents;
  const team = isTeamExport(x);
  const who = (row: CsvRow) => (team ? [row.member ? x.memberNames.get(row.member) ?? '' : ''] : []);
  const lines = [line([
    ...(team ? [t('Mitglied')] : []),
    t('Datum'), t('Beginn'), t('Ende'), t('Abgerechnet (h:mm:ss)'), t('Abgerechnet (h)'), t('Gerundet (h)'),
    t('Eigene Zeit'), t('Agent-Zeit'), t('Überschneidung'), t('Quellen'), t('Beschreibung'), t('Stundensatz'), t('Betrag'),
    t('Beginn (UTC)'), t('Ende (UTC)'),
  ])];
  for (const row of x.rows) {
    const raw = row.humanMs + row.agentMs;
    const d = row.interval.end - row.interval.start;
    lines.push(line([
      ...who(row),
      isoDate(row.interval.start, timeZone), clock(row.interval.start), clock(row.interval.end),
      exactDuration(d), hours(d), hours(row.roundedMs),
      exactDuration(row.humanMs), exactDuration(row.agentMs), exactDuration(Math.max(0, raw - d)),
      row.sources.join(', '), row.descriptions.join(' / '),
      rate === null ? '' : money(rate), rate === null ? '' : money(amountCents(row.roundedMs, rate)),
      new Date(row.interval.start).toISOString(), new Date(row.interval.end).toISOString(),
    ]));
  }
  const billed = x.rows.reduce((n, r) => n + r.interval.end - r.interval.start, 0);
  const human = x.rows.reduce((n, r) => n + r.humanMs, 0);
  const agent = x.rows.reduce((n, r) => n + r.agentMs, 0);
  lines.push(line([
    ...(team ? [''] : []),
    t('Summe'), '', '', exactDuration(billed), hours(billed), hours(x.rounded?.roundedMs ?? billed),
    exactDuration(human), exactDuration(agent), exactDuration(Math.max(0, human + agent - billed)),
    '', '', rate === null ? '' : money(rate), x.rounded ? money(x.rounded.amountCents) : '', '', '',
  ]));
  return `﻿${lines.join('\r\n')}\r\n`;
}

export function fileStamp(range: Interval, timeZone: string): string {
  const first = isoDate(range.start, timeZone);
  const last = isoDate(range.end - 1, timeZone);
  return first === last ? first : `${first}_${last}`;
}

export function projectCsvName(x: ProjectExport, timeZone: string): string {
  const safe = x.project.name.replaceAll('/', '-').replaceAll(':', '-');
  return `Tellary – ${safe} – ${fileStamp(x.range, timeZone)}.csv`;
}

/** The report table: one row per group plus the total. */
export function reportCsv(t: Translate, report: ReportModel, lang: Lang): string {
  const { decimal, line } = csvTools(lang);
  const hours = (ms: number) => decimal(ms / 3_600_000, 4);
  const overlaps = groupingOverlaps(report.grouping);
  const header = [groupingLabel(t, report.grouping), t('Abgerechnet (h)'), t('Summe (h)'), t('Überschneidung (h)'), t('Eigene Zeit (h)'), t('Agent-Zeit (h)'), t('Abrechenbar (h)')];
  if (!overlaps) header.push(t('Betrag'));
  if (report.usage) header.push(t('Tokens'), t('Kosten laut Anbieter (USD)'));
  const lines = [line(header)];
  const row = (label: string, totals: ReportModel['total'], usage: ReportModel['usage']) => {
    const cells = [label, hours(totals.billedMs), hours(totals.rawMs), hours(Math.max(0, totals.rawMs - totals.billedMs)), hours(totals.humanMs), hours(totals.agentMs), hours(totals.billableMs)];
    if (!overlaps) cells.push(decimal(totals.amountCents / 100, 2));
    if (report.usage) {
      cells.push(usage ? String(totalTokens(usage)) : '');
      cells.push(usage?.costMicroUSD != null ? decimal(usage.costMicroUSD / 1_000_000, 2) : '');
    }
    return line(cells);
  };
  for (const r of report.rows) lines.push(row(r.label, r.totals, r.usage));
  lines.push(row(t('Gesamt'), report.total, report.usage));
  return `﻿${lines.join('\r\n')}\r\n`;
}

export function reportCsvName(t: Translate, report: ReportModel, timeZone: string): string {
  return `Tellary – ${t('Bericht')} – ${groupingLabel(t, report.grouping)} – ${fileStamp(report.range, timeZone)}.csv`;
}

/**
 * Invoice-ready summaries per currency, based on merged billable time rounded as each project's export setting says,
 * with token cost lines (spec §9c) when given.
 */
export function invoice(ws: Workspace, perProject: Map<string, ProjectTotals>, vatBasisPoints: number,
  tokens: ReadonlyMap<string, TokenCostLine> = new Map()): InvoiceSummary[] {
  const order = [...ws.projects].sort((a, b) => a.name.localeCompare(b.name)).map((p) => p.id);
  const currencies = new Map(ws.projects.map((p) => [p.id, p.currency]));
  return invoiceSummaries(perProject, billingProjects(ws), currencies, order, vatBasisPoints, tokens);
}

/** Token costs per billable project for an invoice (spec §9c): empty while the workspace does not bill them. Needs usage. */
export function invoiceTokenLines(ws: Workspace, data: RangeData, range: Interval): Map<string, TokenCostLine> {
  const settings = tokenSettings(ws);
  if (!settings) return new Map();
  const sessions = data.sessions.map((s) => ({
    project: s.projectId ?? null, startedAt: s.startedAt, costMicroUSD: usageSummary(data.usage.get(s.id) ?? []).costMicroUSD ?? 0,
  }));
  return tokenCosts(sessions, billingProjects(ws), settings, new Map(ws.projects.map((p) => [p.id, p.currency])), range);
}

/** A Stripe invoice's lines: the summary's lines, described as the paper shows them. */
export function stripeLines(t: Translate, fmt: Formats, ws: Workspace, summary: InvoiceSummary): InvoiceLineInput[] {
  const name = (id: string) => ws.projects.find((p) => p.id === id)?.name ?? id;
  return [
    ...summary.lines.map((line): InvoiceLineInput => ({
      kind: 'time', projectId: line.project, amountCents: line.amountCents,
      description: line.rateCents !== null
        ? t('%@ · %@ h × %@', name(line.project), fmt.decimalHours(line.roundedMs, 2), fmt.money(line.rateCents, summary.currency, 2))
        : t('%@ · %@ h', name(line.project), fmt.decimalHours(line.roundedMs, 2)),
    })),
    ...summary.tokenLines.map((line): InvoiceLineInput => ({
      kind: 'tokens', projectId: line.project, amountCents: line.amountCents,
      description: t('KI-Token-Kosten · %@ (%@)', name(line.project), fmt.money(line.usdCents, 'USD', 2)),
    })),
  ];
}
